import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { vehiclesAPI, depotsAPI } from '../services/api';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Truck, 
  Search,
  Plus,
  Loader2,
  Calendar,
  Gauge,
  Upload,
  X,
  Image,
  FileText,
  Pencil,
  Eye,
  Trash2,
  AlertCircle,
  FileSpreadsheet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VehicleCard = ({ vehicle, onClick, onToggleActive, canToggle }) => {
  const isExpiringSoon = vehicle.license_expiry && 
    new Date(vehicle.license_expiry) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const isExpired = vehicle.license_expiry && 
    new Date(vehicle.license_expiry) < new Date();
  const isInactive = vehicle.is_active === false;

  return (
    <Card 
      className={`cursor-pointer hover:shadow-md transition-all ${isInactive ? 'opacity-60 border-dashed' : ''}`}
      onClick={onClick}
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center">
            <Truck className="w-6 h-6 text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-mono text-lg font-bold text-slate-900">
                {vehicle.registration_number}
              </span>
              <Badge variant="outline" className="text-xs">
                {vehicle.vehicle_type}
              </Badge>
              {isInactive ? (
                <Badge className="bg-slate-200 text-slate-700">Inactive</Badge>
              ) : isExpired ? (
                <Badge className="bg-red-100 text-red-800">Expired</Badge>
              ) : isExpiringSoon ? (
                <Badge className="bg-amber-100 text-amber-800">Expiring</Badge>
              ) : (
                <Badge className="bg-green-100 text-green-800">Active</Badge>
              )}
            </div>
            <p className="text-slate-600">{vehicle.make} {vehicle.model} ({vehicle.year})</p>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
              <span className="flex items-center gap-1">
                <Gauge className="w-4 h-4" />
                {vehicle.current_km?.toLocaleString() || 0} km
              </span>
              {vehicle.license_expiry && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  License: {new Date(vehicle.license_expiry).toLocaleDateString()}
                </span>
              )}
              {vehicle.port_permit_expiry && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Port: {new Date(vehicle.port_permit_expiry).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
          {canToggle && (
            <Button
              size="sm"
              variant="outline"
              className={isInactive ? 'text-emerald-700 border-emerald-300 hover:bg-emerald-50' : 'text-slate-700 border-slate-300 hover:bg-slate-50'}
              onClick={(e) => { e.stopPropagation(); onToggleActive?.(vehicle); }}
              data-testid={`toggle-active-vehicle-${vehicle.id}`}
            >
              {isInactive ? 'Activate' : 'Deactivate'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const emptyVehicle = {
  registration_number: '',
  vin: '',
  make: '',
  model: '',
  vehicle_type: 'Truck',
  year: new Date().getFullYear(),
  pbs: 'No',
  gvm: '',
  tare: '',
  payload: '',
  gcm: '',
  gcp: '',
  front_axle_payload: '',
  rear_axle_payload: '',
  authorized_passengers: 0,
  depot_id: '',
  service_interval_km: 10000,
  service_interval_months: 12,
  last_service_date: '',
  last_service_km: '',
  default_service_provider: '',
  license_expiry: '',
  port_permit_expiry: '',
  cof_expiry: '',
};

export const VehiclesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isSupervisor, isAdmin } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyVehicle);
  const [activeTab, setActiveTab] = useState('details');
  const fileInputRef = useRef(null);
  const [uploadType, setUploadType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [vehiclesRes, depotsRes] = await Promise.all([
          vehiclesAPI.list({ include_inactive: showInactive }),
          depotsAPI.list(),
        ]);
        setVehicles(vehiclesRes.data);
        setDepots(depotsRes.data);
        
        // Check if editing
        const editId = searchParams.get('edit');
        if (editId) {
          const vehicleToEdit = vehiclesRes.data.find(v => v.id === editId);
          if (vehicleToEdit) {
            handleEdit(vehicleToEdit);
          }
        }
        
        // Check if adding new vehicle
        const addNew = searchParams.get('add');
        if (addNew === 'true') {
          setEditingVehicle(null);
          setFormData({
            ...emptyVehicle,
            depot_id: depotsRes.data[0]?.id || ''
          });
          setActiveTab('details');
          setShowDialog(true);
        }
      } catch (error) {
        console.error('Failed to fetch vehicles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams, showInactive]);

  const filteredVehicles = vehicles.filter(v => {
    const searchLower = searchQuery.toLowerCase();
    return v.registration_number?.toLowerCase().includes(searchLower) ||
           v.make?.toLowerCase().includes(searchLower) ||
           v.model?.toLowerCase().includes(searchLower) ||
           v.vin?.toLowerCase().includes(searchLower);
  });

  const handleAdd = () => {
    setEditingVehicle(null);
    setFormData({
      ...emptyVehicle,
      depot_id: depots[0]?.id || ''
    });
    setActiveTab('details');
    setShowDialog(true);
  };

  const handleToggleActive = async (vehicle) => {
    const isCurrentlyActive = vehicle.is_active !== false;
    const action = isCurrentlyActive ? 'deactivate' : 'activate';
    const confirmMsg = isCurrentlyActive
      ? `Deactivate ${vehicle.registration_number}? It will be hidden from active fleet lists but records remain intact.`
      : `Re-activate ${vehicle.registration_number}?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      if (isCurrentlyActive) {
        await vehiclesAPI.delete(vehicle.id);
      } else {
        await vehiclesAPI.restore(vehicle.id);
      }
      toast.success(`Vehicle ${action}d`);
      const vehiclesRes = await vehiclesAPI.list({ include_inactive: showInactive });
      setVehicles(vehiclesRes.data);
    } catch (err) {
      console.error(`Failed to ${action}:`, err);
      toast.error(err.response?.data?.detail || `Failed to ${action} vehicle`);
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      registration_number: vehicle.registration_number || '',
      vin: vehicle.vin || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      vehicle_type: vehicle.vehicle_type || 'Truck',
      year: vehicle.year || new Date().getFullYear(),
      pbs: vehicle.pbs || 'No',
      gvm: vehicle.gvm || '',
      tare: vehicle.tare || '',
      payload: vehicle.payload || '',
      gcm: vehicle.gcm || '',
      gcp: vehicle.gcp || '',
      front_axle_payload: vehicle.front_axle_payload || '',
      rear_axle_payload: vehicle.rear_axle_payload || '',
      authorized_passengers: vehicle.authorized_passengers || 0,
      depot_id: vehicle.depot_id || depots[0]?.id || '',
      service_interval_km: vehicle.service_interval_km || 10000,
      service_interval_months: vehicle.service_interval_months || 12,
      last_service_date: vehicle.last_service_date?.slice(0, 10) || '',
      last_service_km: vehicle.last_service_km || '',
      default_service_provider: vehicle.default_service_provider || '',
      license_expiry: vehicle.license_expiry?.slice(0, 10) || '',
      port_permit_expiry: vehicle.port_permit_expiry?.slice(0, 10) || '',
      cof_expiry: vehicle.cof_expiry?.slice(0, 10) || '',
    });
    setActiveTab('details');
    setShowDialog(true);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.registration_number || !formData.vin || !formData.make || !formData.model) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      
      // Prepare data
      const submitData = {
        ...formData,
        pbs: formData.pbs || 'No',
        gvm: parseFloat(formData.gvm) || 0,
        tare: parseFloat(formData.tare) || 0,
        payload: parseFloat(formData.payload) || 0,
        gcm: formData.gcm ? parseFloat(formData.gcm) : null,
        gcp: formData.gcp ? parseFloat(formData.gcp) : null,
        front_axle_payload: formData.front_axle_payload ? parseFloat(formData.front_axle_payload) : null,
        rear_axle_payload: formData.rear_axle_payload ? parseFloat(formData.rear_axle_payload) : null,
        authorized_passengers: parseInt(formData.authorized_passengers) || 0,
        service_interval_km: parseInt(formData.service_interval_km) || 10000,
        service_interval_months: parseInt(formData.service_interval_months) || 12,
        last_service_km: formData.last_service_km ? parseInt(formData.last_service_km) : 0,
        last_service_date: formData.last_service_date ? new Date(formData.last_service_date).toISOString() : null,
        default_service_provider: formData.default_service_provider || null,
        license_expiry: formData.license_expiry ? new Date(formData.license_expiry).toISOString() : null,
        port_permit_expiry: formData.port_permit_expiry ? new Date(formData.port_permit_expiry).toISOString() : null,
        cof_expiry: formData.cof_expiry ? new Date(formData.cof_expiry).toISOString() : null,
      };

      let savedVehicle = null;
      if (editingVehicle) {
        await vehiclesAPI.update(editingVehicle.id, submitData);
        toast.success('Vehicle updated successfully');
      } else {
        const response = await vehiclesAPI.create(submitData);
        savedVehicle = response.data;
        toast.success('Vehicle added! You can now upload documents.');
      }
      
      // Refresh list
      const response = await vehiclesAPI.list();
      setVehicles(response.data);
      
      // If this was a new vehicle, re-open in edit mode with Documents tab
      if (savedVehicle) {
        const newVehicle = response.data.find(v => v.id === savedVehicle.id);
        if (newVehicle) {
          setEditingVehicle(newVehicle);
          setFormData({
            registration_number: newVehicle.registration_number || '',
            vin: newVehicle.vin || '',
            make: newVehicle.make || '',
            model: newVehicle.model || '',
            vehicle_type: newVehicle.vehicle_type || 'Truck',
            year: newVehicle.year || new Date().getFullYear(),
            pbs: newVehicle.pbs || 'No',
            gvm: newVehicle.gvm || '',
            tare: newVehicle.tare || '',
            payload: newVehicle.payload || '',
            gcm: newVehicle.gcm || '',
            gcp: newVehicle.gcp || '',
            front_axle_payload: newVehicle.front_axle_payload || '',
            rear_axle_payload: newVehicle.rear_axle_payload || '',
            authorized_passengers: newVehicle.authorized_passengers || 0,
            depot_id: newVehicle.depot_id || '',
            service_interval_km: newVehicle.service_interval_km || 10000,
            license_expiry: newVehicle.license_expiry?.slice(0, 10) || '',
            port_permit_expiry: newVehicle.port_permit_expiry?.slice(0, 10) || '',
            cof_expiry: newVehicle.cof_expiry?.slice(0, 10) || '',
          });
          setActiveTab('documents');
          // Keep dialog open for document upload
        } else {
          setShowDialog(false);
        }
      } else {
        setShowDialog(false);
      }
    } catch (error) {
      console.error('Failed to save vehicle:', error);
      toast.error(error.response?.data?.detail || 'Failed to save vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingVehicle || !uploadType) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      setUploading(true);
      // Use axios so the request picks up auth + X-Environment headers from
      // defaults (raw fetch bypasses them and would route to the wrong DB).
      await axios.post(
        `${API_URL}/api/vehicles/${editingVehicle.id}/upload?document_type=${uploadType}`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Document uploaded successfully');
      
      // Refresh vehicle data
      const vehiclesRes = await vehiclesAPI.list();
      setVehicles(vehiclesRes.data);
      const updated = vehiclesRes.data.find(v => v.id === editingVehicle.id);
      if (updated) setEditingVehicle(updated);
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      setUploadType(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerUpload = (type) => {
    setUploadType(type);
    fileInputRef.current?.click();
  };

  const handleDeleteFile = async (fileUrl, docType, fieldName) => {
    if (!editingVehicle) return;
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/vehicles/${editingVehicle.id}/document`, {
        params: { file_url: fileUrl, document_type: docType },
      });
      setEditingVehicle({
        ...editingVehicle,
        [fieldName]: (editingVehicle[fieldName] || []).filter(u => u !== fileUrl),
      });
      // Refresh the vehicles list so other views stay in sync.
      try {
        const vehiclesRes = await vehiclesAPI.list();
        setVehicles(vehiclesRes.data);
      } catch {
        /* non-fatal: the delete already succeeded */
      }
      toast.success('Document deleted');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // Stats
  const totalVehicles = vehicles.length;
  const expiringCount = vehicles.filter(v => {
    if (!v.license_expiry) return false;
    const expiry = new Date(v.license_expiry);
    const threshold = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return expiry < threshold && expiry > new Date();
  }).length;
  const expiredCount = vehicles.filter(v => 
    v.license_expiry && new Date(v.license_expiry) < new Date()
  ).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
            FLEET INVENTORY
          </h1>
          <p className="text-slate-500 mt-1">Manage your vehicle and trailer fleet</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-600 select-none" data-testid="show-inactive-vehicles-label">
            <Switch
              checked={showInactive}
              onCheckedChange={setShowInactive}
              data-testid="show-inactive-vehicles-toggle"
            />
            Show inactive
          </label>
          <Button 
            variant="outline" 
            onClick={() => vehiclesAPI.exportExcel()} 
            className="gap-2"
            data-testid="export-vehicles-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </Button>
          {isSupervisor && (
            <Button onClick={handleAdd} className="gap-2" data-testid="add-vehicle-btn">
              <Plus className="w-4 h-4" />
              Add Vehicle/Trailer
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
              <Truck className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{totalVehicles}</p>
              <p className="text-sm text-slate-500">Total Fleet</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{expiringCount}</p>
              <p className="text-sm text-slate-500">Expiring Soon</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-sm flex items-center justify-center">
              <Calendar className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{expiredCount}</p>
              <p className="text-sm text-slate-500">Expired</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search by registration, make, model, or VIN..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="vehicle-search"
        />
      </div>

      {/* Vehicle List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredVehicles.map((vehicle) => (
          <VehicleCard 
            key={vehicle.id} 
            vehicle={vehicle}
            onClick={() => handleEdit(vehicle)}
            canToggle={isSupervisor}
            onToggleActive={handleToggleActive}
          />
        ))}
      </div>

      {filteredVehicles.length === 0 && (
        <div className="text-center py-12">
          <Truck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No vehicles found</h3>
          <p className="text-slate-500 mt-1">
            {searchQuery ? 'Try a different search term' : 'Add your first vehicle to get started'}
          </p>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingVehicle ? 'EDIT VEHICLE/TRAILER' : 'ADD NEW VEHICLE/TRAILER'}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="weights">Weights & Specs</TabsTrigger>
              <TabsTrigger value="documents" disabled={!editingVehicle}>Documents</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Registration No. *</Label>
                  <Input
                    value={formData.registration_number}
                    onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                    placeholder="NP 12345"
                    data-testid="reg-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>VIN Number *</Label>
                  <Input
                    value={formData.vin}
                    onChange={(e) => setFormData({...formData, vin: e.target.value})}
                    placeholder="VIN123456789"
                    data-testid="vin-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Make *</Label>
                  <Input
                    value={formData.make}
                    onChange={(e) => setFormData({...formData, make: e.target.value})}
                    placeholder="Volvo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Model *</Label>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    placeholder="FH16"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={formData.vehicle_type} onValueChange={(v) => setFormData({...formData, vehicle_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Truck">Truck</SelectItem>
                      <SelectItem value="Trailer">Trailer</SelectItem>
                      <SelectItem value="Horse">Horse</SelectItem>
                      <SelectItem value="Tanker">Tanker</SelectItem>
                      <SelectItem value="Flatbed">Flatbed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Depot</Label>
                  <Select value={formData.depot_id} onValueChange={(v) => setFormData({...formData, depot_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select depot" />
                    </SelectTrigger>
                    <SelectContent>
                      {depots.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>PBS (Performance Based Standards)</Label>
                  <Select value={formData.pbs || 'No'} onValueChange={(v) => setFormData({...formData, pbs: v})}>
                    <SelectTrigger data-testid="pbs-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Authorized Passengers</Label>
                  <Input
                    type="number"
                    value={formData.authorized_passengers}
                    onChange={(e) => setFormData({...formData, authorized_passengers: parseInt(e.target.value) || 0})}
                    min="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>License Expiry</Label>
                  <Input
                    type="date"
                    value={formData.license_expiry}
                    onChange={(e) => setFormData({...formData, license_expiry: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Port Permit Expiry</Label>
                  <Input
                    type="date"
                    value={formData.port_permit_expiry}
                    onChange={(e) => setFormData({...formData, port_permit_expiry: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>COF Expiry</Label>
                  <Input
                    type="date"
                    value={formData.cof_expiry}
                    onChange={(e) => setFormData({...formData, cof_expiry: e.target.value})}
                  />
                </div>
              </div>

              {/* Service Master Data Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold text-slate-700 mb-3">Service Master Data</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Service Interval (km)</Label>
                    <Input
                      type="number"
                      value={formData.service_interval_km}
                      onChange={(e) => setFormData({...formData, service_interval_km: e.target.value})}
                      placeholder="10000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Service Interval (months)</Label>
                    <Input
                      type="number"
                      value={formData.service_interval_months}
                      onChange={(e) => setFormData({...formData, service_interval_months: e.target.value})}
                      placeholder="12"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label>Last Service Date</Label>
                    <Input
                      type="date"
                      value={formData.last_service_date}
                      onChange={(e) => setFormData({...formData, last_service_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Service KM</Label>
                    <Input
                      type="number"
                      value={formData.last_service_km}
                      onChange={(e) => setFormData({...formData, last_service_km: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <Label>Default Service Provider</Label>
                  <Input
                    value={formData.default_service_provider}
                    onChange={(e) => setFormData({...formData, default_service_provider: e.target.value})}
                    placeholder="e.g., ABC Motors Workshop"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Authorized Passengers</Label>
                  <Input
                    type="number"
                    value={formData.authorized_passengers}
                    onChange={(e) => setFormData({...formData, authorized_passengers: e.target.value})}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="weights" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>GVM (kg)</Label>
                  <Input
                    type="number"
                    value={formData.gvm}
                    onChange={(e) => setFormData({...formData, gvm: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tare (kg)</Label>
                  <Input
                    type="number"
                    value={formData.tare}
                    onChange={(e) => setFormData({...formData, tare: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payload (kg)</Label>
                  <Input
                    type="number"
                    value={formData.payload}
                    onChange={(e) => setFormData({...formData, payload: e.target.value})}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>GCM (kg)</Label>
                  <Input
                    type="number"
                    value={formData.gcm}
                    onChange={(e) => setFormData({...formData, gcm: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>GCP (kg)</Label>
                  <Input
                    type="number"
                    value={formData.gcp}
                    onChange={(e) => setFormData({...formData, gcp: e.target.value})}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Front Axle Group Payload (kg)</Label>
                  <Input
                    type="number"
                    value={formData.front_axle_payload}
                    onChange={(e) => setFormData({...formData, front_axle_payload: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rear Axle Group Payload (kg)</Label>
                  <Input
                    type="number"
                    value={formData.rear_axle_payload}
                    onChange={(e) => setFormData({...formData, rear_axle_payload: e.target.value})}
                    placeholder="0"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="documents" className="space-y-4 mt-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleUpload}
                className="hidden"
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
              />
              
              {!editingVehicle && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Save the vehicle first before uploading documents.
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                {/* Vehicle Photos */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Vehicle Photos
                    </Label>
                    {editingVehicle && (
                      <Button size="sm" variant="outline" onClick={() => triggerUpload('vehicle_photo')} disabled={uploading}>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </Button>
                    )}
                  </div>
                  {editingVehicle?.vehicle_photos?.length > 0 ? (
                    <div className="space-y-1 mt-2">
                      {editingVehicle.vehicle_photos.map((url, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <button
                            onClick={async () => {
                              const w = window.open('about:blank', '_blank');
                              try {
                                const r = await axios.get(`${API_URL}${url}`, {responseType:'blob'});
                                const b = new Blob([r.data], {type: r.data.type});
                                if(w) w.location.href = window.URL.createObjectURL(b);
                              } catch { if(w) w.close(); toast.error('File not found'); }
                            }}
                            className="flex-1 flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer text-left"
                          >
                            <Eye className="w-3 h-3" />
                            {url.split('/').pop()}
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteFile(url, 'vehicle_photo', 'vehicle_photos')}
                              className="text-red-500 hover:text-red-700"
                              title="Delete"
                              data-testid={`del-vehicle-photo-${idx}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No files uploaded</div>
                  )}
                </Card>

                {/* Data Plates */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Data Plates
                    </Label>
                    {editingVehicle && (
                      <Button size="sm" variant="outline" onClick={() => triggerUpload('data_plate')} disabled={uploading}>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </Button>
                    )}
                  </div>
                  {editingVehicle?.data_plate_photos?.length > 0 ? (
                    <div className="space-y-1 mt-2">
                      {editingVehicle.data_plate_photos.map((url, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <button
                            onClick={async () => {
                              const w = window.open('about:blank', '_blank');
                              try {
                                const r = await axios.get(`${API_URL}${url}`, {responseType:'blob'});
                                const b = new Blob([r.data], {type: r.data.type});
                                if(w) w.location.href = window.URL.createObjectURL(b);
                              } catch { if(w) w.close(); toast.error('File not found'); }
                            }}
                            className="flex-1 flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer text-left"
                          >
                            <Eye className="w-3 h-3" />
                            {url.split('/').pop()}
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteFile(url, 'data_plate', 'data_plate_photos')}
                              className="text-red-500 hover:text-red-700"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No files uploaded</div>
                  )}
                </Card>

                {/* NATIS Documents */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      NATIS Documents
                    </Label>
                    {editingVehicle && (
                      <Button size="sm" variant="outline" onClick={() => triggerUpload('natis')} disabled={uploading}>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </Button>
                    )}
                  </div>
                  {editingVehicle?.natis_documents?.length > 0 ? (
                    <div className="space-y-1 mt-2">
                      {editingVehicle.natis_documents.map((url, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <button
                            onClick={async () => {
                              const w = window.open('about:blank', '_blank');
                              try {
                                const r = await axios.get(`${API_URL}${url}`, {responseType:'blob'});
                                const b = new Blob([r.data], {type: r.data.type});
                                if(w) w.location.href = window.URL.createObjectURL(b);
                              } catch { if(w) w.close(); toast.error('File not found'); }
                            }}
                            className="flex-1 flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer text-left"
                          >
                            <Eye className="w-3 h-3" />
                            {url.split('/').pop()}
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteFile(url, 'natis', 'natis_documents')}
                              className="text-red-500 hover:text-red-700"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No files uploaded</div>
                  )}
                </Card>

                {/* License Discs */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      License Discs
                    </Label>
                    {editingVehicle && (
                      <Button size="sm" variant="outline" onClick={() => triggerUpload('license_disc')} disabled={uploading}>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </Button>
                    )}
                  </div>
                  {editingVehicle?.license_disc_photos?.length > 0 ? (
                    <div className="space-y-1 mt-2">
                      {editingVehicle.license_disc_photos.map((url, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <button
                            onClick={async () => {
                              const w = window.open('about:blank', '_blank');
                              try {
                                const r = await axios.get(`${API_URL}${url}`, {responseType:'blob'});
                                const b = new Blob([r.data], {type: r.data.type});
                                if(w) w.location.href = window.URL.createObjectURL(b);
                              } catch { if(w) w.close(); toast.error('File not found'); }
                            }}
                            className="flex-1 flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer text-left"
                          >
                            <Eye className="w-3 h-3" />
                            {url.split('/').pop()}
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteFile(url, 'license_disc', 'license_disc_photos')}
                              className="text-red-500 hover:text-red-700"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No files uploaded</div>
                  )}
                </Card>

                {/* Port Permit Documents */}
                <Card className="p-4 col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Port Permit Documents
                    </Label>
                    {editingVehicle && (
                      <Button size="sm" variant="outline" onClick={() => triggerUpload('port_permit')} disabled={uploading}>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </Button>
                    )}
                  </div>
                  {editingVehicle?.port_permit_documents?.length > 0 ? (
                    <div className="space-y-1 mt-2">
                      {editingVehicle.port_permit_documents.map((url, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <button
                            onClick={async () => {
                              const w = window.open('about:blank', '_blank');
                              try {
                                const r = await axios.get(`${API_URL}${url}`, {responseType:'blob'});
                                const b = new Blob([r.data], {type: r.data.type});
                                if(w) w.location.href = window.URL.createObjectURL(b);
                              } catch { if(w) w.close(); toast.error('File not found'); }
                            }}
                            className="flex-1 flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer text-left"
                          >
                            <Eye className="w-3 h-3" />
                            {url.split('/').pop()}
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteFile(url, 'port_permit', 'port_permit_documents')}
                              className="text-red-500 hover:text-red-700"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No files uploaded</div>
                  )}
                </Card>
              </div>

              {uploading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  <span className="ml-2 text-slate-500">Uploading...</span>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingVehicle ? 'Update' : 'Add Vehicle'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehiclesPage;
